import _ from 'lodash';
import { createSelector } from 'reselect';

import { SUBSCRIPTION_STATES } from '../reducers/subscriptions';
import { ACCOUNT_ACCESS } from 'app/app-constants';

export const getSubscriptionsData = state => _.get(state, 'subscriptions');

// Subscription statuses for effective active statuses that can
// manage / update other user statuses.
const CAN_MANAGE_STATUSES = ['active', 'non_renewing'];

function userCanManage(subscriptions) {
  const status = _.get(subscriptions, 'attributes.status');
  const messagingState = _.get(subscriptions, 'attributes.messaging_state');
  return _.includes(CAN_MANAGE_STATUSES, status) ||
    (status === 'in_trial' && messagingState === 'trial_paying');
}

const isSubscriptionsEnabled = (state) => {
  const features = _.get(state, 'common.features');
  return features && !!features.SAPPHIRE_SUN;
};

const getSubscription = createSelector(
  isSubscriptionsEnabled,
  getSubscriptionsData,
  // TODO: how do we handle multiple subscriptions in here? Also should the reducer
  // extract this instead of just plopping the whole blob down here?
  (enabled, settings) => (enabled ? (_.get(settings, 'current.data') || {}) : {}),
);

const isSubscriptionActive = createSelector(
  isSubscriptionsEnabled,
  getSubscription,
  (enabled, subscriptions) => enabled && userCanManage(subscriptions),
);

const getSubscriptionRole = createSelector(
  isSubscriptionsEnabled,
  getSubscription,
  (enabled, subscriptions) => (enabled && _.get(subscriptions, 'attributes.role')),
);

const isSubscriber = createSelector(
  isSubscriptionsEnabled,
  isSubscriptionActive,
  getSubscriptionRole,
  (enabled, active, role) => (enabled && active && role === 'subscriber'),
);

function isLoadingState(value) {
  return value === SUBSCRIPTION_STATES.loading;
}

const isSubscriptionDataLoading = createSelector(
  isSubscriptionsEnabled,
  getSubscriptionsData,
  (enabled, subscriptions) => (
    // Subscription data obviously "loaded" if not enabled
    enabled && isLoadingState(_.get(subscriptions, 'state'))
  ));

const getSubscriptionsTotalAllowedUsers = createSelector(
  getSubscription,
  subscriptions => (_.get(subscriptions, 'attributes.total_users') || 0),
);

const getSubscriptionsAddedUsers = createSelector(
  isSubscriptionsEnabled,
  getSubscriptionsData,
  (enabled, subscriptions) => (
    enabled ? (_.get(subscriptions, 'addedUsers.data') || []) : []
  ),
);

const getSubscriptionsActiveAddedUsers = createSelector(
  getSubscriptionsAddedUsers,
  addedUsers => (
    _.filter(addedUsers, u => _.get(u, 'attributes.status') === 'active')));

const getSubscriptionIsRestricted = createSelector(
  getSubscriptionsData,
  isSubscriptionsEnabled,
  (subscriptions, enabled) => {
    const currentSubscription = _.get(subscriptions, 'current.data');
    const isOwner = !_.isEmpty(_.get(currentSubscription, 'attributes.external'));
    const accountAccess = _.get(currentSubscription, 'attributes.additional_users_access_status');

    return enabled && !isOwner && accountAccess === ACCOUNT_ACCESS.RESTRICTED;
  }
)

const canManageSubscriptions = createSelector(
  isSubscriptionsEnabled,
  getSubscriptionsData,
  (enabled, data) => {
    const subscriptions = _.get(data, 'current.data');
    return enabled && userCanManage(subscriptions);
  },
);

const isManagePending = createSelector(
  isSubscriptionsEnabled,
  getSubscriptionsData,
  (enabled, data) => {
    if (!enabled) { return false; }

    return _.get(data, 'managePending', false);
  },
);

const getManageSubscriptionsLink = createSelector(
  canManageSubscriptions,
  getSubscription,
  (enabled, subscriptions) => {
    if (!enabled) {
      return '';
    }

    return _.get(subscriptions, 'links.portal');
  },
);

export const subscriptions = {
  isEnabled: isSubscriptionsEnabled,
  isActive: isSubscriptionActive,
  isSubscriber,
  isLoading: isSubscriptionDataLoading,
  current: getSubscription,
  getTotalUsers: getSubscriptionsTotalAllowedUsers,
  getAddedUsers: getSubscriptionsActiveAddedUsers,
  getIsRestricted: getSubscriptionIsRestricted,
  getManageLink: getManageSubscriptionsLink,
  canManage: canManageSubscriptions,
  isManagePending,
};



// WEBPACK FOOTER //
// ./app/selectors/subscriptions.js